import { defineStore } from "pinia";
import { useCallsignLookupsStore } from "@/stores/CallsignLookupStore";
import type { LookupServiceProviderType } from "@/types";
import { useLocalStorage } from '@vueuse/core'
import type { VisibilityState } from '@tanstack/vue-table'

export const QSO_TABLE_PAGE_SIZES = [25, 50, 100, 500] as const;

function normalizeQsoTablePageSizeValue(size: number): number {
  return QSO_TABLE_PAGE_SIZES.some(pageSize => pageSize === size)
    ? size
    : QSO_TABLE_PAGE_SIZES[0];
}

export const useSettingStore = defineStore("settings", {
  state: () => ({
    lookupProvider: "HAMDB" as LookupServiceProviderType,
    isOnline: false,
    panelOpen: false,
    expandedMode: false,
    enableVirtualKeyboard: useLocalStorage("pinia/setting/enableVirtualKeyboard", false),
    darkMode: useLocalStorage("pinia/setting/darkMode",
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ),
    openDyslexic: useLocalStorage("pinia/setting/openDyslexic", false),
    doLookups: useLocalStorage("pinia/setting/doLookups", true),
    displayToasts: useLocalStorage("pinia/setting/displayToasts", true),
    localTime: useLocalStorage("pinia/setting/localTime", false),
    autoFillParkPrefix: useLocalStorage("pinia/setting/autoFillParkPrefix", false),
    activeProfileID: useLocalStorage("pinia/setting/activeProfileID", ""),
    logbookTableSort: useLocalStorage("pinia/tables/logbookTableSort", [{ "id": "createdAt", "desc": true }]),
    qsoTableSort: useLocalStorage("pinia/tables/qsoTableSort", [{ "id": "qsoDateTime", "desc": true }]),
    qsoTablePageSize: useLocalStorage(
      "pinia/tables/qsoTablePageSize",
      normalizeQsoTablePageSizeValue(25)
    ),
    qsoTableColumnVisibility: useLocalStorage<Record<string, VisibilityState>>(
      "pinia/tables/qsoTableColumnVisibility",
      {}
    ),
    lastUsedTemplate: useLocalStorage("pinia/setting/lastUsedTemplate", "GENERIC"),
    mapStyle: useLocalStorage("pinia/setting/mapStyle", "default"),
    showQthOnMap: useLocalStorage("pinia/setting/showQthOnMap", true),
    showLabelsOnMap: useLocalStorage("pinia/setting/showLabelsOnMap", true),
    lastKnownVersion: useLocalStorage("pinia/setting/lastKnownVersion", ""),
    showAboutModal: false,
  }),
  actions: {
    setActiveProfileID(id: string) {
      this.activeProfileID = id;
    },
    setLookupProvider(provider: LookupServiceProviderType) {
      this.lookupProvider = provider;
      useCallsignLookupsStore().initProvider(provider);
    },
    setMapStyle(style: string) {
      this.mapStyle = style;
    },
    setShowQthOnMap(show: boolean) {
      this.showQthOnMap = show;
    },
    setShowLabelsOnMap(show: boolean) {
      this.showLabelsOnMap = show;
    },
    toggleDoLookups() {
      this.doLookups = !this.doLookups;
    },
    toggleDisplayToasts() {
      this.displayToasts = !this.displayToasts;
    },
    togglePanel() {
      this.panelOpen = !this.panelOpen;
    },
    toggleLocalTime() {
      this.localTime = !this.localTime;
    },
    toggleAutoFillParkPrefix() {
      this.autoFillParkPrefix = !this.autoFillParkPrefix;
    },
    toggleEnableVirtualKeyboard() {
      this.enableVirtualKeyboard = !this.enableVirtualKeyboard;
    },
    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      if (this.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    },

    initDarkMode() {
      if (this.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    },
    toggleOpenDyslexic() {
      this.openDyslexic = !this.openDyslexic;
      if (this.openDyslexic) {
        document.documentElement.classList.add('font-opendyslexic');
      } else {
        document.documentElement.classList.remove('font-opendyslexic');
      }
    },
    initOpenDyslexic() {
      if (this.openDyslexic) {
        document.documentElement.classList.add('font-opendyslexic');
      } else {
        document.documentElement.classList.remove('font-opendyslexic');
      }
    },
    openAboutModal() {
      this.showAboutModal = true;
    },
    closeAboutModal() {
      this.showAboutModal = false;
    },
    checkForVersionUpdate(currentVersion: string) {
      if (this.lastKnownVersion && this.lastKnownVersion !== currentVersion) {
        this.openAboutModal();
      }
      this.lastKnownVersion = currentVersion;
    },
    normalizeQsoTablePageSize(size: number) {
      return normalizeQsoTablePageSizeValue(size);
    },
    setQsoTablePageSize(size: number) {
      const normalizedSize = normalizeQsoTablePageSizeValue(size);
      this.qsoTablePageSize = normalizedSize;
      return normalizedSize;
    },
    getQsoTableColumnVisibility(template: string): VisibilityState | undefined {
      return this.qsoTableColumnVisibility[template];
    },
    setQsoTableColumnVisibility(template: string, visibility: VisibilityState) {
      this.qsoTableColumnVisibility = {
        ...this.qsoTableColumnVisibility,
        [template]: visibility
      };
    },
  },
});
